<template>
  <b-container fluid class="p-0 m-2 m-md-4">
    <b-card class="mb-2 mb-md-3">
      <div slot="header">Inputs
        <div class="card-header-actions">
          <font-awesome-icon
                size="lg"
                class="ml-2 h-100"
                :style="{'cursor': 'pointer', 'user-select': 'none', 'color': 'gray'}"
                :icon="inputsCollapsed ? 'chevron-down' : 'chevron-up'"
                v-b-toggle.inputsCollapse />
        </div>
      </div>
      <b-collapse visible id="inputsCollapse" @show="inputsCollapsed = false" @hide="inputsCollapsed = true">
        <b-row>
          <b-col sm="12" md="6" lg="6" xl="4">
            <gcu-dropdown v-model="selectedGCU" :multiple="false" cacheKey="armcontroller" :invalid="gcuError"></gcu-dropdown>
          </b-col>
          <b-col sm="12" md="6" lg="6" xl="4">
            <tracker-dropdown v-model="selectedTracker" :gcu="selectedGCU ? selectedGCU.id : null" :multiple="false" :disabled="disabled.tracker"
              cacheKey="armcontroller" :invalid="trackerError">
            </tracker-dropdown>
          </b-col>
          <b-col sm="12" md="6" lg="6" xl="4">
            <b-form-group label="Armature" :horizontal="true">
              <multiselect v-model="selectedArm" :multiple="false" placeholder="Select Armature" :options="armatures" :disabled="disabled.armature"
                  selectLabel="" deselectLabel="Remove">
              </multiselect>
            </b-form-group>
          </b-col>
        </b-row>
      </b-collapse>
    </b-card>

    <b-card class="m-0">
      <div slot="header">Controller</div>
      <b-row>
        <b-col sm="12" md="8">
          <b-table striped bordered responsive="sm" :items="armInfo" :fields="fields" :per-page="10" hover fixed small class="my-2 mx-md-2"></b-table>
        </b-col>
        <b-col sm="12" md="4">
          <div class="control-container mx-auto my-2">
            <div class="d-flex justify-content-center">
              <b-button @click="moveArm(1, -1)" class="btn-lg ctrl-btn ms-btn-primary" :disabled="!controllerEnabled"><font-awesome-icon icon="arrow-up" size="lg" /></b-button>
            </div>
            <div class="d-flex justify-content-between mt-2">
              <b-button @click="moveArm(0, -1)" class="btn-lg ms-btn-primary" :disabled="!controllerEnabled"><font-awesome-icon icon="arrow-left" size="lg" /></b-button>
              <b-button @click="moveArm(0, 1)" class="btn-lg ms-btn-primary" :disabled="!controllerEnabled"><font-awesome-icon icon="arrow-right" size="lg" /></b-button>
            </div>
            <div class="d-flex justify-content-center mt-2">
              <b-button @click="moveArm(1, 1)" class="btn-lg ms-btn-primary" :disabled="!controllerEnabled"><font-awesome-icon icon="arrow-down" size="lg" /></b-button>
            </div>
            <div class="my-5">
              <vue-slider :min="0.1" :max="10" :interval="0.1" v-model="stepSize" tooltip="hover"
                :tooltipStyle="sliderOptions.tooltipStyle" :processStyle="sliderOptions.processStyle">
              </vue-slider>
              <b-form-input type="number" min="0.1" max="10" step="0.1" class="my-2" :value="stepSize" @input="handleStepInput"></b-form-input>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-modal v-model="showDialog" title="Tracker Status" ok-title="Yes" cancel-title="No" @ok="handleOk" @cancel="showDialog = false">
        The tracker has to be in idle mode when moving an armature.
        Would you like to put the tracker in idle mode?
    </b-modal>
  </b-container>
</template>

<script>
import GcuDropdown from '../components/GcuDropdown';
import TrackerDropdown from '../components/TrackerDropdown';
import Multiselect from 'vue-multiselect';
import VueSlider from 'vue-slider-component'
import { buildMovePayload, parseTrackerStatus, parseReedRanges, parseRegister } from '../helpers/trackerHelper';
import { setInterval, clearInterval } from 'timers';
import { mapGetters, mapState } from 'vuex';

const map = {
  error: 0,
  azi: 1,
  ele: 2,
  aziReed: 3,
  eleReed: 4,
  trackerStatus: 5,
  timestamp: 6
};

const errorMap = {
  '00': 'No error',
  '01': 'Azimuth: RS always OFF',
  '02': 'Azimuth: RS always ON',
  '03': 'Azimuth: Failed to find east-side on-edge',
  '04': 'Azimuth: Failed to find west-side off-edge',
  '05': 'Azimuth: Failed to find west-side on-edge',
  '06': 'Elevation: RS always OFF',
  '07': 'Elevation: RS always ON',
  '08': 'Elevation: Failed to find lower on-edge',
  '09': 'Elevation: Failed to find upper off-edge',
  '0A': 'Elevation: Failed to find upper on-edge',
  '0B': 'Stow mode error: Motor angle at stow position is more than 2 deg. from 90',
  '0C': 'RS ON range is out of valid range (valid RS ON range: 0< Elv RS-ON < 30 degree, 0< Azi RS-ON < 40 degree)',
  '0D': 'Incorrect zone: reed switch is not engaged but zone is 2',
  '0E': 'Backtracking table error: Empty table',
  '0F': 'Backtracking table error: Empty row in the table',
  '10': 'Backtracking table format error: declination array length does not match table size',
  '11': 'Backtracking table format error: azimuth and elevation have different hour angle index',
  '12': 'Zone info flash memory: failed to write',
  '13': 'Stow mode error: Reed switch is not engaged at stow position',
  '14': 'On-board temperature above 65°C',
  '15': 'Azimuth quick search failed',
  '16': 'Elevation quick search failed',
  'FF': 'Armature is disabled'
};

let subscription = null;
let intervalId = null;
let azimuthReed = 'Unknown';
let elevationReed = 'Unknown';

export default {
  name: 'armcontroller',
  components: {
    GcuDropdown,
    TrackerDropdown,
    Multiselect,
    VueSlider
  },
  data() {
    return {
      selectedGCU: null,
      selectedTracker: null,
      selectedArm: null,
      armatures: [1, 2, 3, 4],
      disabled: {
        gcu: true,
        tracker: true,
        armature: true
      },
      stepSize: 5,
      armInfo: [
        { name: 'Error', data: 'Unknown' },
        { name: 'Azimuth', data: 'Unknown' },
        { name: 'Elevation', data: 'Unknown' },
        { name: 'Azimuth Reed', data: 'Unknown' },
        { name: 'Elevation Reed', data: 'Unknown' },
        { name: 'Tracker Status', data: 'Unknown' },
        { name: 'Data Timestamp', data: 'Unknown' }
      ],
      fields: [
        {key: 'name'},
        {key: 'data'}
      ],
      sliderOptions: {
        tooltipStyle: {
          "backgroundColor": "#FF9E1B",
          "borderColor": "#FF9E1B"
        },
        processStyle: {
          "backgroundColor": "#FF9E1B"
        }
      },
      showDialog: false,
      gcuError: false,
      trackerError: false,
      firstResponse: false,
      inputsCollapsed: false
    };
  },
  methods: {
    startStatusInterval() {
      if (this.selectedGCU && this.gcuStatus(this.selectedGCU.id) !== 'ONLINE') {
          this.showError({ message: 'The GCU did not respond.' });
          this.gcuError = true;
          this.stopStatusInterval();
          return;
      }

      this.gcuError = false;

      if (intervalId === null && this.selectedArm != null) {
          console.log('Interval Started');
          this.checkArmStatus();
          intervalId = setInterval(() => this.checkArmStatus(), 5000);
      }
    },
    stopStatusInterval() {
      if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
          console.log('Interval Cleared');
      }
    },
    // AXIS: 0 (azimuth) or 1 (elevation). DIR: -1 for up, left and 1 for right, down
    moveArm(axis, dir) {
      const { selectedGCU, selectedTracker, selectedArm, stepSize } = this;

      if (!this.controllerEnabled) return;

      if (this.armInfo[map.trackerStatus].data === 'idle_mode') {
          const payload = buildMovePayload(selectedArm, axis, dir, stepSize);
          const command = `7F1${('00'+ selectedTracker.id).slice(-3)}${this.$broker.armControllerReplyQueue}${payload}`;
          this.$broker.sendGCUWithCommand(command, selectedGCU.id);
      } else {
          this.showDialog = true;
      }
    },
    checkArmStatus() {
      const { selectedGCU, selectedTracker } = this;

      // Send command for tracker status
      let command = `702${('00'+ selectedTracker.id).slice(-3)}${this.$broker.armControllerReplyQueue}`;
      this.$broker.sendGCUWithCommand(command, selectedGCU.id);

      // Send command for reed ranges
      command = `714${('00'+ selectedTracker.id).slice(-3)}${this.$broker.armControllerReplyQueue}`;
      this.$broker.sendGCUWithCommand(command, selectedGCU.id);
      console.log('Sending');
    },
    handleOk() {
      // Sends command to put tracker in idle mode
      const { selectedGCU, selectedTracker } = this;
      let command = `709${('00'+ selectedTracker.id).slice(-3)}${this.$broker.armControllerReplyQueue}`;
      this.$broker.sendGCUWithCommand(command, selectedGCU.id);
    },
    handleStepInput(value) {
      value = parseFloat(value);
      if (!value || value < 0.1) this.stepSize = 0.1;
      else if (value > 10) this.stepSize = 10;
      else this.stepSize = value;
    }
  },
  created() {
    // Create a new reply queue if one doesn't exist
    if (!this.$broker.armControllerReplyQueue) {
      this.$broker.armControllerReplyQueue = this.$broker.genUserQueueName();
    }

    this.$broker.subscribe(this.$broker.armControllerReplyQueue, (msg) => {
      msg.ack();

      const { selectedArm, armInfo } = this;
      const msgCode = msg.headers.datatype.slice(0, 3);
      const trackerCode = parseInt(msg.headers.datatype.substr(3));
      const gcuCode = msg.headers.gcuserial;
      const msgBody = msg.body;

      if (gcuCode === this.selectedGCU.id && trackerCode === this.selectedTracker.id && selectedArm) {
        // If there is an error, stop the interval
        if (msgBody.includes('did not respond (5 Attempts). Please check if LCU is powered on.') && msgCode != '7F1' ) {
            this.showError({ message: msgBody });
            this.trackerError = true;
            this.stopStatusInterval();
            return;
        }

        this.trackerError = false;
        this.firstResponse = true;

        // Parse the message depending on the code and update the state
        switch(msgCode) {
          case '702':
            const trackerStatus = parseTrackerStatus(msgBody);
            const error = parseRegister(trackerStatus.register, selectedArm);

            armInfo.splice(map.error, 1, { name: 'Error', data: errorMap[error] });
            azimuthReed = trackerStatus.arms[selectedArm].azi_reed_engaged ? 'Engaged' : 'Disengaged';
            elevationReed = trackerStatus.arms[selectedArm].ele_reed_engaged ? 'Engaged' : 'Disengaged';
            armInfo.splice(map.azi, 1, { name: 'Azimuth', data: trackerStatus.arms[selectedArm].azi });
            armInfo.splice(map.ele, 1, { name: 'Elevation', data: trackerStatus.arms[selectedArm].ele });
            armInfo.splice(map.trackerStatus, 1, { name: 'Tracker Status', data: trackerStatus.status });
            armInfo.splice(map.timestamp, 1, { name: 'Data Timestamp', data: trackerStatus.timestamp.toLocaleTimeString() });

            break;
          case '714':
            const reedRanges = parseReedRanges(msgBody);

            const aziString = `${azimuthReed} onRange: ${reedRanges[selectedArm].azi.on.toFixed(3)}
              offRange: ${reedRanges[selectedArm].azi.off.toFixed(3)}`;

            const eleString = `${elevationReed} onRange: ${reedRanges[selectedArm].ele.on.toFixed(3)}
              offRange: ${reedRanges[selectedArm].ele.off.toFixed(3)}`;

            armInfo.splice(map.aziReed, 1, { name: 'Azimuth Reed', data: aziString });
            armInfo.splice(map.eleReed, 1, { name: 'Elevation Reed', data: eleString });

            break;
          default:
            // ignore
          }
        }
    }).then(sub => subscription = sub);
  },
  beforeDestroy() {
    this.stopStatusInterval();

    if (subscription) {
      subscription.unsubscribe();
    }
  },
  computed: {
    ...mapState(['userIdle']),
    ...mapGetters('savanna/gcus', {
      gcuStatus: 'getGCUStatus'
    }),
    controllerEnabled() {
      return this.selectedArm && this.firstResponse && !this.gcuError && !this.trackerError;
    }
  },
  watch: {
    userIdle(oldVal, newVal){
      if(oldVal === newVal) return;

      if(this.userIdle) {
        this.stopStatusInterval();
      } else {
        this.startStatusInterval();
      }
    },
    selectedGCU() {
      this.selectedTracker = null;

      this.gcuError = false;

      if (this.selectedGCU === null) {
          this.disabled.tracker = true;
      } else {
          this.disabled.tracker = false;
      }
    },
    selectedTracker() {
      this.selectedArm = null;

      this.trackerError = false;
      this.firstResponse = false;

      if (this.selectedTracker === null) {
          this.disabled.armature = true;
      } else {
          this.disabled.armature = false;
      }
    },
    selectedArm() {
      // Clear interval if it exists
      this.stopStatusInterval();

      // Reset arm info state
      this.armInfo = [
        { name: 'Error', data: 'Unknown' },
        { name: 'Azimuth', data: 'Unknown' },
        { name: 'Elevation', data: 'Unknown' },
        { name: 'Azimuth Reed', data: 'Unknown' },
        { name: 'Elevation Reed', data: 'Unknown' },
        { name: 'Tracker Status', data: 'Unknown' },
        { name: 'Data Timestamp', data: 'Unknown' }
      ];

      // If the new selectedArm is null, we don't need to request data
      if (this.selectedArm === null) return;

      // Start the checking status interval
      this.startStatusInterval();
    }
  },
  notifications: {
    showError: {
      type: 'error',
      title: 'No Response Error'
    }
  }
}
</script>

<style>
  .control-container {
    width: 200px;
  }
</style>
